import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { setIsAddQualification } from "@/module/education/stores/educationSlice";
import useQualification from "../api/useQualification";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import SelectController from "@/components/ui/others/SelectController";
import { useEffect, useMemo, useState } from "react";
import { monthOptions } from "@/config/constants";
import DocumentMultyUploadSection, {
  DocumentFile,
} from "@/components/doc/DocumentMultyUpload";
import { useQualificationMutation } from "../api/useQualificationMutation";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { toast } from "sonner";
import { refetchLeadQuals } from "../api/useQualificationByLead";

export const QualificationSchema = z.object({
  level: z.string().min(1, "Qualification level is required"),
  universityCollege: z.string().min(1, "Institution name is required"),
  scoreValue: z.string().min(1, "Score is required"),
  qualification: z.string(),
  specs: z.string(),
  yearOfPassout: z.string().min(1, "Year of passout is required"),
  monthOfPassout: z.string().min(1, "Month of passout is required"),
});

type QualificationFormData = z.infer<typeof QualificationSchema>;

export default function AddQualificationModal() {
  const dispatch = useAppDispatch();
  const [documents, setDocuments] = useState<DocumentFile[]>([]);
  const { lead, refetchLead } = useActiveDataMutation();

  const { isAddQualification } = useAppSelector(
    (state) => state.educationSlice
  );

  const { levelSelects, qualiSelects, qualifications } = useQualification();
  const { createLeadQualification } = useQualificationMutation();

  const {
    register,
    control,
    watch,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<QualificationFormData>({
    resolver: zodResolver(QualificationSchema),
    defaultValues: {
      level: "",
      qualification: "",
      specs: "",
      universityCollege: "",
      scoreValue: "",
      yearOfPassout: "",
      monthOfPassout: "",
    },
    mode: "onChange",
  });

  const selectedLevelId = watch("level");
  const selectedQualiId = watch("qualification");

  useEffect(() => {
    if (selectedLevelId) {
      setValue("qualification", "");
      setValue("specs", "");
    }
  }, [selectedLevelId, setValue]);

  useEffect(() => {
    if (selectedQualiId) {
      setValue("specs", "");
    }
  }, [selectedQualiId, setValue]);

  const filteredQualifications = useMemo(() => {
    if (!selectedLevelId) return [];
    return qualiSelects.filter((q: any) => {
      const raw = qualifications.find((rq: any) => String(rq.id) === q.value);
      return String(raw?.qualificationLevelId) === selectedLevelId;
    });
  }, [selectedLevelId, qualiSelects, qualifications]);

  const filteredSpecializations = useMemo(() => {
    if (!selectedQualiId) return [];
    const selectedQualObj = qualifications.find(
      (q: any) => String(q.id) === selectedQualiId
    );
    return (
      selectedQualObj?.specializations?.map((s: any) => ({
        value: String(s.id),
        name: s.name,
      })) || []
    );
  }, [selectedQualiId, qualifications]);

  const handleAddDocuments = (files: FileList) => {
    const newDocuments: DocumentFile[] = Array.from(files).map((file) => {
      const fileExtension = file.name.split(".").pop()?.toUpperCase() || "FILE";
      return {
        id: `${Date.now()}-${Math.random()}`,
        file,
        fileName: file.name,
        size: file.size,
        type: fileExtension,
        createdAt: new Date().toISOString(),
      };
    });
    setDocuments((prev) => [...prev, ...newDocuments]);
  };

  const handleRemoveDocument = (doc: DocumentFile) => {
    setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
  };

  const handleClose = () => {
    reset();
    setDocuments([]);
    dispatch(setIsAddQualification(false));
  };

  const onSubmit = (data: QualificationFormData) => {
    const formData = new FormData();

    if (!lead.id) return toast.error("Lead Not Fetched Properly");

    formData.append("leadId", lead.id);
    formData.append("level", data.level);
    formData.append("qualification", data.qualification);
    formData.append("specs", data.specs);
    formData.append("universityCollege", data.universityCollege);
    formData.append("scoreValue", data.scoreValue);
    formData.append("yearOfPassout", data.yearOfPassout);
    formData.append("monthOfPassout", data.monthOfPassout);
    formData.append("attachedScope", "qualificationScore");

    const levelName =
      levelSelects?.find((val: any) => val.value == data.level)?.name ||
      "Qualifications";
    formData.append("category", levelName);
    console.log(levelName, "levelName");

    // Exclude all explicitly appended fields to prevent duplication
    const excludedKeys = [
      "leadId",
      "level",
      "qualification",
      "specs",
      "universityCollege",
      "scoreValue",
      "yearOfPassout",
      "monthOfPassout",
      "attachedScope",
    ];
    // 1. Append standard form fields
    Object.entries(data).forEach(([key, value]) => {
      if (excludedKeys.includes(key)) return;
      // Handle different value types safely
      if ((value as any) instanceof Date) {
        formData.append(key, value.toString());
      } else if (value !== null && value !== undefined) {
        // Convert numbers/booleans to string
        formData.append(key, String(value));
      }
    });

    // 2. Append Files
    // Iterate through your local 'documents' state and append the actual File object
    documents.forEach((doc) => {
      if (doc.file) {
        // "documents" is the key your backend middleware (e.g., Multer) must expect
        formData.append("files", doc.file);
      }
    });

    console.log("Submitting FormData...");

    // 3. Invoke Mutation
    // Assuming 'createQualification' or similar mutation name
    createLeadQualification.mutate(formData, {
      onSuccess: () => {
        handleClose();
        refetchLead();
        refetchLeadQuals();
      },
      onError: (error) => {
        console.error("Upload failed", error);
      },
    });
  };

  return (
    <Dialog open={isAddQualification} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto rounded-[24px] p-0 gap-0 border-none shadow-2xl"
        showCloseButton={false}
      >
        <DialogHeader className="px-4 pt-[16px] pb-[11px] border-b border-[#e6e8e7] sticky top-0 bg-white z-20">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-col gap-[2px] flex-1">
              <p className="text-[20px] font-medium text-black tracking-[-0.8px]">
                Add qualification
              </p>
              <p className="text-[14px] text-[#505e59]">
                Add the leads academic qualification details
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="rounded-full"
            >
              <X className="size-4" strokeWidth={1.5} />
            </Button>
          </div>
        </DialogHeader>

        <div className="bg-[#f8f8f8] px-4 py-4 flex flex-col gap-4">
          {/* Level and Qualification - Side by Side */}
          <div className="flex justify-center items-start gap-5">
            <SelectController
              className="w-[50%]"
              errors={errors}
              control={control}
              name="level"
              title="Level"
              options={levelSelects}
            />

            <SelectController
              className="w-[50%]"
              errors={errors}
              control={control}
              name="qualification"
              title="Qualification"
              options={filteredQualifications}
              disabled={!selectedLevelId}
            />
          </div>

          {/* Specialization - Full Width */}
          <SelectController
            errors={errors}
            control={control}
            name="specs"
            title="Specialization"
            options={filteredSpecializations}
            disabled={!selectedQualiId}
          />

          {/* University/College and Score Value - Side by Side */}
          <div className="flex justify-center items-start gap-5">
            <div className="flex flex-col gap-[6px] w-[50%]">
              <Label htmlFor="universityCollege">University/College</Label>
              <Input
                {...register("universityCollege")}
                id="universityCollege"
                placeholder="Enter institution name"
              />
              {errors.universityCollege && (
                <span className="text-xs text-red-500 font-medium">
                  {errors.universityCollege.message}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-[6px] w-[50%]">
              <Label htmlFor="scoreValue">Score Value</Label>
              <Input
                {...register("scoreValue")}
                id="scoreValue"
                placeholder="Enter percentage/grade"
              />
              {errors.scoreValue && (
                <span className="text-xs text-red-500 font-medium">
                  {errors.scoreValue.message}
                </span>
              )}
            </div>
          </div>

          {/* Year and Month of Passout - Side by Side */}
          <div className="flex justify-center items-start gap-5">
            <div className="flex flex-col gap-[6px] w-[50%]">
              <Label htmlFor="yearOfPassout">Year of Passout</Label>
              <Input
                {...register("yearOfPassout")}
                id="yearOfPassout"
                placeholder="Enter year (e.g., 2024)"
              />
              {errors.yearOfPassout && (
                <span className="text-xs text-red-500 font-medium">
                  {errors.yearOfPassout.message}
                </span>
              )}
            </div>

            <SelectController
              className="w-[50%] m-h-[50px]"
              errors={errors}
              control={control}
              name="monthOfPassout"
              title="Month of Passout"
              options={monthOptions}
            />
          </div>

          {/* Document Upload Section */}
          <DocumentMultyUploadSection
            documents={documents}
            onAdd={handleAddDocuments}
            onRemove={handleRemoveDocument}
            maxDocuments={4}
          />
        </div>

        {/* Action Footer */}
        <DialogFooter className="px-4 pb-4 pt-4 border-t border-[#e6e8e7] flex justify-end gap-2 bg-white rounded-b-[24px] sticky bottom-0 z-20">
          <Button
            variant="ghost"
            type="button"
            onClick={handleClose}
            className="w-[120px] text-[#da2a46] hover:bg-red-50 rounded-[12px]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit(onSubmit)}
            variant="primary"
            className="h-[40px] px-6 rounded-[12px] font-medium shadow-sm transition-all active:scale-95"
          >
            Save Qualification
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
